import { UNKNOWN_ERROR_MESSAGE } from '@/common/ui/UnknownErrorMessage';
import api from '@/db/apiClient';
import { CheckCircleIcon } from '@phosphor-icons/react';
import { Alert, AlertDescription } from '@shadcn/components/alert';
import { Button } from '@shadcn/components/button';
import { Input } from '@shadcn/components/input';
import { sendPasswordResetEmail } from 'firebase/auth';
import { AlertCircleIcon } from 'lucide-react';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthViewWrapper, AuthWrapper, LogoWrapper } from './AuthStyles';
import logo from '/ui/new-spellcaster-logo.svg';

export default function AdminForgotPassword() {
  const navigate = useNavigate();

  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState<ReactNode | null>(null);
  const [success, setSuccess] = useState(false);
  const [email, setEmail] = useState<string>('');
  const [cooldown, setCooldown] = useState(0);
  const timerRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  function startCooldown() {
    setCooldown(30);
    timerRef.current = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    const isValidEmail = (email: string) => {
      return /\S+@\S+\.\S+/.test(email.trim());
    };

    if (!isValidEmail(email)) {
      setError('Please enter a valid email address.');
      return;
    }

    setLoading(true);
    try {
      await sendPasswordResetEmail(api.asEducator.auth, email);
      setLoading(false);
      setSuccess(true);
      startCooldown();
    } catch (error) {
      console.error('Error sending password reset email:', error);
      setLoading(false);
      const code =
        error && typeof error === 'object' && 'code' in error
          ? error.code
          : 'unknown';
      console.log(code);
      if (code === 'auth/user-not-found') {
        setError('No user found with this email address.');
      } else {
        setError(UNKNOWN_ERROR_MESSAGE);
      }
    }
  }

  return (
    <AuthViewWrapper>
      <LogoWrapper onClick={() => navigate('/')}>
        <img src={logo} alt="logo" />
      </LogoWrapper>
      <AuthWrapper>
        {success ? (
          <>
            <div className="flex flex-col gap-2 items-center text-sm text-muted-foreground text-center">
              <CheckCircleIcon size={40} className="text-green-600 mb-2" />
              <p>
                We've sent an email to <strong>{email}</strong> with
                instructions to reset your password.
              </p>
              <p>
                If you don't see it soon, check your spam folder or resend
                below.
              </p>
            </div>
            <Button
              onClick={handleSubmit}
              loading={isLoading}
              disabled={cooldown > 0}
              className="w-full mt-2"
            >
              {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend email'}
            </Button>
            <Button
              type="button"
              variant="link"
              className="p-0 h-fit"
              onClick={() => navigate('/login')}
            >
              Back to log in
            </Button>
          </>
        ) : (
          <>
            {error && (
              <Alert variant="destructive">
                <AlertCircleIcon size={16} className="ml-2" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <p className="text-sm text-muted-foreground text-center">
              Enter your email address and we'll send you an email with
              instructions to reset your password.
            </p>

            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              disabled={isLoading}
            />
            <Button
              type="submit"
              onClick={handleSubmit}
              loading={isLoading}
              className="w-full mt-2"
            >
              Reset Password
            </Button>
            <Button
              type="button"
              variant="link"
              disabled={isLoading}
              className="p-0 h-fit"
              onClick={() => navigate('/login')}
            >
              Back to log in
            </Button>
          </>
        )}
      </AuthWrapper>
    </AuthViewWrapper>
  );
}
